import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'

import { ApiError } from '../api/client'
import { PageHeader } from '../components/Page'
import { endpoints, type Order, type Subscription } from '../api/endpoints'
import { formatRupiah } from '../money'
import { errorMessage, t } from '../strings'

const dateOnly = new Intl.DateTimeFormat('id-ID', { dateStyle: 'long' })
const when = (iso: string) => dateOnly.format(new Date(iso))

function stateText(s: Subscription): string {
  switch (s.state) {
    case 'trial':
      return t.stateTrial(Math.max(s.days_left, 0))
    case 'active':
      return t.stateActive(Math.max(s.days_left, 0))
    case 'grace':
      return t.stateGrace(Math.abs(s.days_left))
    case 'expired':
      return t.stateExpired
  }
}

/** A calm line in the app shell when the owner should act; nothing when all is well. */
export function SubscriptionBanner() {
  const sub = useQuery({ queryKey: ['subscription'], queryFn: endpoints.subscription, staleTime: 60_000 })
  const s = sub.data
  if (!s) return null
  let text: string | null = null
  if (s.state === 'expired') text = t.bannerExpired
  else if (s.state === 'grace') text = t.bannerGrace
  else if (s.state === 'trial' && s.days_left <= 5) text = t.bannerTrialEnding(Math.max(s.days_left, 0))
  else if (s.state === 'active' && s.days_left <= 5) text = t.stateActive(Math.max(s.days_left, 0))
  if (!text) return null
  return (
    <p className={s.state === 'expired' ? 'error banner' : 'warning banner'} role="status">
      {text} <Link to="/langganan">{t.bannerAction}</Link>
    </p>
  )
}

/** Shown where a report would be, when the server answers `subscription_required`. */
export function ReportsLocked() {
  return (
    <section className="card warn-card">
      <h2>{t.reportsLocked}</h2>
      <p className="muted">{t.reportsLockedBody}</p>
      <Link to="/langganan">{t.bannerAction}</Link>
    </section>
  )
}

function OrderStatus({ orderId }: { orderId: string }) {
  const qc = useQueryClient()
  // The redirect back from the payment page grants nothing: this only WATCHES what the server
  // learns from Midtrans (ADR-0008 clause 4).
  const order = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => endpoints.order(orderId),
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? 3000 : false),
  })
  const o = order.data
  if (order.isError) return <p className="error">{errorMessage(order.error.message)}</p>
  if (!o) return null
  if (o.status === 'paid') {
    void qc.invalidateQueries({ queryKey: ['subscription'] })
    return <PaidNote />
  }
  if (o.status === 'pending')
    return (
      <section className="card" aria-live="polite">
        <strong>{t.waitingPayment}</strong>
        <p className="muted small">{t.waitingHint}</p>
        {o.redirect_url && (
          <a href={o.redirect_url} className="button-link">
            {t.openPayment}
          </a>
        )}
      </section>
    )
  return <p className="warning">{o.status === 'refunded' ? t.payRefunded : t.payFailed}</p>
}

function PaidNote() {
  const sub = useQuery({ queryKey: ['subscription'], queryFn: endpoints.subscription })
  const until = sub.data?.paid_until
  return <p className="ok banner">{until ? t.paidOk(when(until)) : t.paidOk('-')}</p>
}

export function SubscriptionPage() {
  const qc = useQueryClient()
  const [params] = useSearchParams()
  const returning = params.get('order')
  const sub = useQuery({ queryKey: ['subscription'], queryFn: endpoints.subscription })
  const [error, setError] = useState<string | null>(null)
  const checkout = useMutation({
    mutationFn: endpoints.createOrder,
    onSuccess: (o: Order) => {
      void qc.invalidateQueries({ queryKey: ['subscription'] })
      if (o.redirect_url) window.location.assign(o.redirect_url)
    },
    onError: (e) => setError(errorMessage(e instanceof ApiError ? e.code : 'network')),
  })
  const [voucher, setVoucher] = useState('')
  const [voucherMsg, setVoucherMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const redeem = useMutation({
    mutationFn: endpoints.redeemVoucher,
    onSuccess: (r) => {
      setVoucher('')
      setVoucherMsg({ ok: true, text: t.voucherOk(r.days) })
      void qc.invalidateQueries({ queryKey: ['subscription'] })
    },
    onError: (e) => setVoucherMsg({ ok: false, text: errorMessage(e instanceof ApiError ? e.code : 'network') }),
  })

  const s = sub.data
  if (sub.isError) return <p className="error">{errorMessage(sub.error.message)}</p>
  if (!s) return <p>{t.loading}</p>

  function submitVoucher(e: FormEvent) {
    e.preventDefault()
    setVoucherMsg(null)
    if (voucher.trim()) redeem.mutate(voucher.trim())
  }

  return (
    <>
      <PageHeader title={t.subTitle} subtitle={t.subSub} back={{ to: '/pengaturan', label: t.navSettings }} />
      {returning && <OrderStatus orderId={returning} />}
      <section className="card hero">
        <div className={s.state === 'expired' ? 'diff' : 'muted'}>{stateText(s)}</div>
        <div className="muted small">{t.validUntil(when(s.entitled_until))}</div>
      </section>

      <section className="card">
        <h2>{t.choosePlan}</h2>
        {!s.checkout_available && <p className="warning">{t.checkoutOff}</p>}
        <ul className="list">
          {s.plans.map((p) => (
            <li key={p.code} className="line">
              <div>
                <div className="line-title">{p.name}</div>
                <div className="muted small">{t.planLine(p.months, p.days)}</div>
              </div>
              <div className="right">
                <div className="amount">{formatRupiah(p.price)}</div>
                <button
                  className="secondary compact"
                  disabled={!s.checkout_available || checkout.isPending}
                  onClick={() => {
                    setError(null)
                    checkout.mutate(p.code)
                  }}
                >
                  {checkout.isPending && checkout.variables === p.code ? t.paying : t.pay}
                </button>
              </div>
            </li>
          ))}
        </ul>
        <p className="muted small">{t.monthNote}</p>
        {error && <p className="error">{error}</p>}
      </section>

      <form className="card" onSubmit={submitVoucher}>
        <h2>{t.voucherTitle}</h2>
        <label>
          {t.voucherLabel}
          <input value={voucher} onChange={(e) => setVoucher(e.target.value.toUpperCase())} autoCapitalize="characters" maxLength={40} />
        </label>
        <button type="submit" className="secondary" disabled={redeem.isPending || voucher.trim() === ''}>
          {t.voucherRedeem}
        </button>
        {voucherMsg && <p className={voucherMsg.ok ? 'ok' : 'error'}>{voucherMsg.text}</p>}
      </form>

      {s.orders.length > 0 && (
        <section className="card">
          <h2>{t.orders}</h2>
          <ul className="list">
            {s.orders.map((o) => (
              <li key={o.order_id} className="line">
                <div>
                  <div className="line-title">{s.plans.find((p) => p.code === o.plan_code)?.name ?? o.plan_code}</div>
                  <div className="muted small">{when(o.created_at)}</div>
                </div>
                <div className="right">
                  <div className="amount">{formatRupiah(o.gross_amount)}</div>
                  <span className={`badge ${o.status === 'paid' ? 'active' : 'revoked'}`}>{t.orderStatus[o.status] ?? o.status}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}
